// MME Analytics Platform - Unified Color Scheme
// Based on the graphical identity from screenshots

export interface ColorScheme {
  // Primary Colors
  primary: string;        // Main brand color - Dark Blue
  secondary: string;      // Secondary brand color - Teal
  accent: string;         // Accent color - Light Blue
  
  // Status Colors
  success: string;        // Green for positive/bullish
  warning: string;        // Orange for warnings/alerts
  error: string;          // Red for negative/bearish
  info: string;           // Blue for information
  
  // Background Colors
  background: string;     // Main background
  surface: string;        // Surface/card background
  surfaceHover: string;   // Surface hover state
  surfaceBorder: string;  // Border color
  
  // Text Colors
  text: string;           // Primary text
  textSecondary: string;  // Secondary text
  textMuted: string;      // Muted text
  textInverse: string;    // Text on colored backgrounds
  
  // Chart Colors
  chartPrimary: string;   // Primary chart color
  chartSecondary: string; // Secondary chart color
  chartSuccess: string;   // Success chart color
  chartWarning: string;   // Warning chart color
  chartError: string;     // Error chart color
  
  // Data Visualization Colors
  boughtCalls: string;    // Green for bought calls
  soldCalls: string;      // Red for sold calls
  boughtPuts: string;     // Blue for bought puts
  soldPuts: string;       // Orange for sold puts
  sentimentPositive: string; // Positive sentiment
  sentimentNegative: string; // Negative sentiment
  sentimentNeutral: string;  // Neutral sentiment
}

// Light Theme Colors
export const lightColors: ColorScheme = {
  // Primary Colors
  primary: '#1e40af',     // Dark Blue
  secondary: '#0d9488',   // Teal
  accent: '#3b82f6',      // Light Blue
  
  // Status Colors
  success: '#059669',     // Green
  warning: '#ea580c',     // Orange
  error: '#dc2626',       // Red
  info: '#0ea5e9',        // Sky Blue
  
  // Background Colors
  background: '#ffffff',  // White
  surface: '#f8fafc',     // Light Gray
  surfaceHover: '#f1f5f9', // Lighter Gray
  surfaceBorder: '#e2e8f0', // Border Gray
  
  // Text Colors
  text: '#0f172a',        // Dark Blue
  textSecondary: '#475569', // Medium Gray
  textMuted: '#64748b',   // Light Gray
  textInverse: '#ffffff', // White
  
  // Chart Colors
  chartPrimary: '#1e40af', // Dark Blue
  chartSecondary: '#0d9488', // Teal
  chartSuccess: '#059669', // Green
  chartWarning: '#ea580c', // Orange
  chartError: '#dc2626',   // Red
  
  // Data Visualization Colors
  boughtCalls: '#059669', // Green
  soldCalls: '#7c3aed',   // Purple
  boughtPuts: '#dc2626',  // Red
  soldPuts: '#ea580c',    // Orange
  sentimentPositive: '#059669', // Green
  sentimentNegative: '#dc2626', // Red
  sentimentNeutral: '#64748b',  // Gray
};

// Dark Theme Colors (Primary theme based on screenshots)
export const darkColors: ColorScheme = {
  // Primary Colors
  primary: '#3b82f6',     // Bright Blue
  secondary: '#0d9488',   // Teal
  accent: '#60a5fa',      // Light Blue
  
  // Status Colors
  success: '#10b981',     // Bright Green
  warning: '#f59e0b',     // Amber
  error: '#ef4444',       // Bright Red
  info: '#06b6d4',        // Cyan
  
  // Background Colors
  background: '#0f172a',  // Dark Blue-Gray
  surface: '#1e293b',     // Slate Gray
  surfaceHover: '#334155', // Lighter Slate
  surfaceBorder: '#475569', // Border Gray
  
  // Text Colors
  text: '#f8fafc',        // Light Gray
  textSecondary: '#cbd5e1', // Medium Light Gray
  textMuted: '#94a3b8',   // Muted Gray
  textInverse: '#0f172a', // Dark Blue
  
  // Chart Colors
  chartPrimary: '#3b82f6', // Bright Blue
  chartSecondary: '#0d9488', // Teal
  chartSuccess: '#10b981', // Bright Green
  chartWarning: '#f59e0b', // Amber
  chartError: '#ef4444',   // Bright Red
  
  // Data Visualization Colors
  boughtCalls: '#10b981', // Bright Green
  soldCalls: '#8b5cf6',   // Purple
  boughtPuts: '#ef4444',  // Bright Red
  soldPuts: '#f59e0b',    // Yellow/Orange
  sentimentPositive: '#10b981', // Bright Green
  sentimentNegative: '#ef4444', // Bright Red
  sentimentNeutral: '#94a3b8',  // Muted Gray
};

// MME Analytics Platform Specific Colors (Based on screenshots)
export const mmeColors: ColorScheme = {
  // Primary Colors - MME Brand
  primary: '#1e40af',     // MME Dark Blue
  secondary: '#0d9488',   // MME Teal
  accent: '#3b82f6',      // MME Light Blue
  
  // Status Colors - Financial Indicators
  success: '#059669',     // Bullish Green
  warning: '#ea580c',     // Alert Orange
  error: '#dc2626',       // Bearish Red
  info: '#0ea5e9',        // Info Blue
  
  // Background Colors - Dark Theme (Primary)
  background: '#0f172a',  // Dark Blue-Gray Background
  surface: '#1e293b',     // Card/Surface Background
  surfaceHover: '#334155', // Hover State
  surfaceBorder: '#475569', // Border Color
  
  // Text Colors - High Contrast
  text: '#f8fafc',        // Primary Text (Light)
  textSecondary: '#cbd5e1', // Secondary Text
  textMuted: '#94a3b8',   // Muted Text
  textInverse: '#0f172a', // Text on Light Backgrounds
  
  // Chart Colors - Data Visualization
  chartPrimary: '#3b82f6', // Primary Chart Color
  chartSecondary: '#0d9488', // Secondary Chart Color
  chartSuccess: '#10b981', // Success Chart Color
  chartWarning: '#f59e0b', // Warning Chart Color
  chartError: '#ef4444',   // Error Chart Color
  
  // Data Visualization Colors - Options Trading (Unified)
  boughtCalls: '#10b981', // Green for Bought Calls (Bullish)
  soldCalls: '#8b5cf6',   // Purple for Sold Calls (Bearish)
  boughtPuts: '#ef4444',  // Red for Bought Puts (Bearish)
  soldPuts: '#f59e0b',    // Yellow/Orange for Sold Puts (Bullish)
  sentimentPositive: '#10b981', // Positive Sentiment
  sentimentNegative: '#ef4444', // Negative Sentiment
  sentimentNeutral: '#94a3b8',  // Neutral Sentiment
};

// Export the MME colors as default
export const defaultColors = mmeColors;

// Helper function to get colors based on theme
export const getColors = (isDark: boolean = true): ColorScheme => {
  return isDark ? mmeColors : lightColors;
};

// Chart color arrays for data visualization
export const chartColorPalette = {
  primary: ['#3b82f6', '#0d9488', '#10b981', '#f59e0b', '#ef4444'],
  options: ['#10b981', '#8b5cf6', '#ef4444', '#f59e0b'], // Bought Calls (Green), Sold Calls (Purple), Bought Puts (Red), Sold Puts (Yellow)
  sentiment: ['#10b981', '#ef4444', '#94a3b8'], // Positive, Negative, Neutral
  flow: ['#3b82f6', '#0d9488', '#10b981', '#f59e0b', '#ef4444', '#06b6d4'],
};

// CSS Variables for easy integration
export const cssVariables = {
  '--mme-primary': '#1e40af',
  '--mme-secondary': '#0d9488',
  '--mme-accent': '#3b82f6',
  '--mme-success': '#059669',
  '--mme-warning': '#ea580c',
  '--mme-error': '#dc2626',
  '--mme-info': '#0ea5e9',
  '--mme-background': '#0f172a',
  '--mme-surface': '#1e293b',
  '--mme-surface-hover': '#334155',
  '--mme-surface-border': '#475569',
  '--mme-text': '#f8fafc',
  '--mme-text-secondary': '#cbd5e1',
  '--mme-text-muted': '#94a3b8',
  '--mme-text-inverse': '#0f172a',
  '--mme-bought-calls': '#10b981',
  '--mme-sold-calls': '#ef4444',
  '--mme-bought-puts': '#0d9488',
  '--mme-sold-puts': '#f59e0b',
};

// Legacy exports for backward compatibility
export const getOptionFlowColors = (isDarkMode: boolean) => {
  const colors = getColors(isDarkMode);
  return {
    callsBought: colors.boughtCalls,    // Green for bought calls
    callsSold: colors.soldCalls,        // Purple for sold calls  
    putsBought: colors.boughtPuts,      // Red for bought puts
    putsSold: colors.soldPuts,          // Orange for sold puts
    totalVolume: '#8b5cf6', // Purple for total volume
  };
};

export const getBidAskColors = (isDarkMode: boolean) => {
  const colors = getColors(isDarkMode);
  return {
    bidColor: colors.chartPrimary,
    askColor: colors.chartSecondary,
  };
};

export const getSentimentColors = (isDarkMode: boolean) => {
  const colors = getColors(isDarkMode);
  return {
    positive: colors.sentimentPositive,
    negative: colors.sentimentNegative,
    neutral: colors.sentimentNeutral,
  };
};

// Legacy color scheme function for backward compatibility
export const getColorScheme = (isDarkMode: boolean): ColorScheme => {
  return getColors(isDarkMode);
}; 